import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { HardDrive, X } from "lucide-react";
import { subscribe } from "../../lib/walletEvents";
import useDialogDismiss from "../../hooks/useDialogDismiss";

// Surfaced when an upload returns 413 / 402 storage_cap_exceeded.
// Pricing v2 added real storage-tier purchases — the modal now routes
// users to /account/storage where they can either purge old matches
// (keep stats, free GB) OR buy an add-on tier (recurring).
export default function StorageCapModal() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const panelRef = useRef(null);
  const close = () => setOpen(false);
  useDialogDismiss({ open, onClose: close, ref: panelRef });

  useEffect(() => {
    return subscribe("storage_cap_exceeded", (payload) => {
      setData(payload);
      setOpen(true);
    });
  }, []);

  if (!open || !data) return null;

  const usedGB = ((data.used_bytes ?? 0) / 1e9).toFixed(2);
  const capGB = ((data.cap_bytes ?? 0) / 1e9).toFixed(2);
  const incomingMB = ((data.incoming_bytes ?? 0) / 1e6).toFixed(1);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-3 sm:p-4"
      role="presentation"
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Storage cap reached"
        className="bg-bg-primary border border-border-default p-5 sm:p-8 w-full max-w-sm sm:max-w-md max-h-[90vh] overflow-y-auto rounded-xl shadow-xl relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={close}
          aria-label="Close"
          className="absolute top-3 right-3 text-text-secondary hover:text-text-primary"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-orange-500/15 flex items-center justify-center shrink-0">
            <HardDrive className="w-5 h-5 text-orange-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-text-primary">Zenstorage cap reached</h3>
            <p className="text-sm text-text-secondary mt-1">
              {data.message || "Your plan's Zenstorage is full."}
            </p>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-3 gap-2 text-sm">
          <div className="bg-bg-secondary/40 rounded-md p-3">
            <p className="text-text-secondary text-xs">Used</p>
            <p className="text-text-primary font-semibold">{usedGB} GB</p>
          </div>
          <div className="bg-bg-secondary/40 rounded-md p-3">
            <p className="text-text-secondary text-xs">Cap</p>
            <p className="text-text-primary font-semibold">{capGB} GB</p>
          </div>
          <div className="bg-bg-secondary/40 rounded-md p-3">
            <p className="text-text-secondary text-xs">Upload</p>
            <p className="text-text-primary font-semibold">{incomingMB} MB</p>
          </div>
        </div>

        <p className="mt-4 text-xs text-text-secondary">
          Two options on the Storage page: delete old match videos (keeps the
          scorecards + stats intact) or add a recurring storage tier from ₹500/mo.
        </p>

        <div className="mt-6 flex gap-2 justify-end">
          <button
            onClick={() => setOpen(false)}
            className="px-4 py-2 rounded-md text-sm text-text-secondary hover:bg-bg-secondary/40"
          >
            Close
          </button>
          <Link
            to="/account/storage"
            onClick={() => setOpen(false)}
            className="px-4 py-2 rounded-md text-sm bg-button-primary text-white hover:opacity-90"
          >
            Manage storage →
          </Link>
        </div>
      </div>
    </div>
  );
}
